import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { AdvancedSettingsBar } from "components/Campaign/CampaignEffects/AdvancedSettingsBar/AdvancedSettingsBar";
import { prepareCleanExpression } from "components/EffectSearch/utils";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as Copy02Icon } from "components/Icons/copy-02.svg";
import { ReactComponent as Pencil02Icon } from "components/Icons/pencil-02.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { CampaignEffect } from "types/campaigns";
import { EffectError } from "./Effect";
import { EffectReadOnly } from "./EffectReadOnly";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    iconsWrapper: {
      display: "flex",
      alignItems: "center",
    },
    iconBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "50%",
      width: theme.spacing(12),
      height: theme.spacing(12),
      background: theme.gray50,
      margin: theme.spacing(0, 2),
      cursor: "pointer",
    },
    conditionWrapper: {
      display: "flex",
      justifyContent: "space-between",
      padding: theme.spacing(6, 8),
    },
    condition: {
      display: "flex",
      alignItems: "center",
      maxWidth: "80%",
      flexWrap: "wrap",
    },
    icon: {
      width: theme.spacing(8),
      height: theme.spacing(8),
      color: theme.gray500,
    },
    error: {
      color: theme.red500,
      margin: theme.spacing(0, 2),
    },
    vectorMargin: {
      margin: theme.spacing(-7, 0, 0, 10),
    },
  }),
);

interface Props {
  remove: () => void;
  edit: () => void;
  duplicate: () => void;
  index: number;
  effect: CampaignEffect;
  error: EffectError;
}
export const EffectReadOnlyWrapper = ({
  remove,
  edit,
  duplicate,
  index,
  effect,
  error,
}: Props) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const { message } = useMessage();
  const isError = !!error;

  const expirationStrategy = effect.unitsExpirationRule?.expirationStrategy;
  const lockStrategy = effect.unitsLockRule?.lockStrategy;
  const cleanExpirationExpression =
    expirationStrategy === "never_expires"
      ? message({ id: "neverExpires" })
      : prepareCleanExpression(
          effect.unitsExpirationRule?.expression as string,
        );
  const cleanPendingExpression =
    lockStrategy === "no_pending"
      ? message({ id: "noPending" })
      : prepareCleanExpression(effect.unitsLockRule?.expression as string);
  const labels = [
    {
      label: message({ id: "expirationSettings" }),
      value: cleanExpirationExpression,
    },
    {
      label: message({ id: "pendingSettings" }),
      value: cleanPendingExpression,
    },
  ];

  return (
    <>
      <div className={classes.conditionWrapper}>
        <div className={classes.condition}>
          <span>{index + 1}.</span>
          <EffectReadOnly effect={effect} data-test-id="effect" error={error} />
        </div>
        <div className={classes.iconsWrapper}>
          {isError && <AlertTriangleIcon className={classes.error} />}
          <div className={classes.iconBox}>
            <Pencil02Icon
              onClick={edit}
              className={classes.icon}
              data-test-id="edit-effect-rule-icon"
            />
          </div>

          <div className={classes.iconBox}>
            <Copy02Icon onClick={duplicate} className={classes.icon} />
          </div>

          <div className={classes.iconBox}>
            <XCloseIcon
              onClick={() => setOpen(true)}
              className={classes.icon}
            />
          </div>
        </div>
        <ConfirmationModal
          action={() => {
            setOpen(false);
            remove();
          }}
          close={() => {
            setOpen(false);
          }}
          cancelText={message({ id: "cancel" })}
          header="deleteEffect"
          info="deleteEffectConfirm"
          open={open}
          submitText="deleteEffect"
        />
      </div>
      {cleanExpirationExpression || cleanPendingExpression ? (
        <AdvancedSettingsBar
          labels={labels}
          vectorMarginClass={classes.vectorMargin}
        />
      ) : null}
    </>
  );
};
